import { addWeeks, subWeeks } from "date-fns"
import { convertToStartOfMonthUTC, isAfter, startOfTodayUTC } from "~/utils/datetime"
import type { FiscalConfig } from "~/utils/fiscalCalendar"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"
import {
  addMissingDatesSalesAndForecast,
  convertWeeklyToFullMonthly,
} from "~/utils/weeklyToMonthly"
import type { TimeSeriesData } from "./types"
import type { SalesAndForecast } from "~/models/variant"

/**
 * Filters time series data by date range based on view mode
 * @param rawData - The raw time series data to filter
 * @param startDate - Start of the date range
 * @param endDate - End of the date range (unused but kept for API consistency)
 * @param monthlyView - Whether to use monthly view filtering
 * @returns Filtered time series data
 */
export function filterByDateRange(
  rawData: TimeSeriesData,
  startDate: Date,
  _endDate: Date,
  monthlyView: boolean,
): TimeSeriesData {
  const filterStartDate = monthlyView ? convertToStartOfMonthUTC(startDate) : subWeeks(startDate, 1)

  return rawData.filter((x) => isAfter(x.date, filterStartDate))
}

/**
 * Processes time series data through the standard transformation pipeline.
 *
 * Core shared logic used by both variant-level and SKU-level calculations.
 *
 * @param rawData - Raw time series data
 * @param startDate - Start date for calculation period
 * @param endDate - End date for calculation period
 * @param monthlyView - Whether to aggregate data monthly (vs weekly)
 * @param includeCurrentMonth - Keep the current month in monthly view even when the range
 *   only partially covers it; it renders as a partial value. When false, a partially
 *   covered current month is trimmed by the full-month filter.
 * @returns Processed time series data ready for row-specific transformations
 */
export function processTimeSeriesData(
  rawData: TimeSeriesData,
  startDate: Date,
  endDate: Date,
  monthlyView: boolean,
  includeCurrentMonth = false,
  fiscalConfig: FiscalConfig | null = getStoredFiscalConfig(),
): TimeSeriesData {
  // Fiscal periods overhang their calendar month by up to a few weeks, so widen the range or the
  // boundary periods lose weeks and get dropped as incomplete.
  const fiscal = monthlyView ? (fiscalConfig ?? null) : null
  const rangeStart = fiscal ? subWeeks(startDate, 5) : startDate
  const rangeEnd = fiscal ? addWeeks(endDate, 5) : endDate

  const filtered = filterByDateRange(rawData, rangeStart, rangeEnd, monthlyView)

  const today = startOfTodayUTC()
  const withFutureFlag: SalesAndForecast[] = filtered.map((d) => ({
    date: d.date,
    quantity: d.quantity,
    future: d.date > today,
  }))

  const withMissingDates = addMissingDatesSalesAndForecast(withFutureFlag, rangeStart, rangeEnd)

  const processed = monthlyView
    ? convertWeeklyToFullMonthly(
        withMissingDates,
        rangeStart,
        rangeEnd,
        includeCurrentMonth,
        fiscal,
      )
    : withMissingDates

  return processed.map((d) => ({
    date: d.date,
    quantity: d.quantity,
  }))
}

/**
 * Rounds a number to the nearest integer, or returns null if input is null/undefined
 */
export function roundQuantity(quantity: number | null | undefined): number | null {
  if (quantity == null) return null
  const rounded = Math.round(quantity)
  return rounded === 0 ? null : rounded
}
